<template>
  <div class="ip" v-click-outside="close">
    <Tooltip :text="$t('dash.overview.dashboards.title_icon')" position="top">
      <span class="ip-trigger" :class="{ open }" role="button" tabindex="0" @click="open = !open" @keydown.enter="open = !open">
        <i class="material-icons" :class="{ 'ip-none': value === 'none' }">{{ value === 'none' ? 'block' : (value || defaultIcon) }}</i>
        <i class="material-icons ip-caret">arrow_drop_down</i>
      </span>
    </Tooltip>

    <div v-if="open" class="ip-pop">
      <div class="ip-search">
        <i class="material-icons">search</i>
        <input ref="search" v-model="query" :placeholder="$t('dash.overview.dashboards.search_icon')" />
      </div>
      <div class="ip-quick">
        <span class="ip-quick-btn" :class="{ active: !value }" role="button" tabindex="0" @click="pick(null)">
          <i class="material-icons">{{ defaultIcon }}</i>{{ $t('dash.overview.dashboards.title_icon_default') }}
        </span>
        <span class="ip-quick-btn" :class="{ active: value === 'none' }" role="button" tabindex="0" @click="pick('none')">
          <i class="material-icons ip-none">block</i>{{ $t('dash.overview.dashboards.title_icon_none') }}
        </span>
      </div>
      <div class="ip-list">
        <template v-for="cat in filtered">
          <p :key="`t-${cat.key}`" class="ip-cat">{{ $t(`dash.overview.dashboards.icon_cat.${cat.key}`) }}</p>
          <div :key="`g-${cat.key}`" class="ip-grid">
            <span
              v-for="ic in cat.icons"
              :key="ic"
              class="ip-icon"
              :class="{ active: value === ic }"
              :title="ic.replace(/_/g, ' ')"
              role="button"
              tabindex="0"
              @click="pick(ic)"
              @keydown.enter="pick(ic)"
            ><i class="material-icons">{{ ic }}</i></span>
          </div>
        </template>
        <p v-if="!filtered.length" class="ip-empty">{{ $t('dash.overview.dashboards.no_icon_found') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import ClickOutside from 'vue-click-outside'
import Tooltip from '../../tooltip/tooltipInit.vue'

// Icônes de la police Material Icons classique utilisée par l'app, par catégorie (mots-clés FR pour la recherche)
const CATEGORIES = [
  { key: 'stats', words: 'stat chiffre graphique courbe performance kpi', icons: ['insert_chart', 'show_chart', 'timeline', 'trending_up', 'trending_down', 'trending_flat', 'pie_chart', 'donut_large', 'equalizer', 'assessment', 'multiline_chart', 'bubble_chart', 'track_changes', 'av_timer', 'data_usage', 'poll', 'format_list_numbered', 'functions'] },
  { key: 'social', words: 'social réseau communauté audience abonné personne', icons: ['people', 'person', 'group', 'group_add', 'person_add', 'public', 'share', 'thumb_up', 'thumb_down', 'favorite', 'favorite_border', 'star', 'star_border', 'grade', 'whatshot', 'mood', 'sentiment_satisfied', 'sentiment_dissatisfied', 'face', 'record_voice_over'] },
  { key: 'content', words: 'contenu publication post média photo vidéo texte', icons: ['library_books', 'description', 'edit', 'create', 'photo', 'image', 'collections', 'photo_camera', 'videocam', 'movie', 'play_circle_outline', 'music_note', 'mic', 'format_quote', 'title', 'short_text', 'subject', 'text_fields', 'attach_file', 'link'] },
  { key: 'communication', words: 'message commentaire inbox discussion notification email', icons: ['chat', 'chat_bubble_outline', 'question_answer', 'forum', 'comment', 'message', 'mail_outline', 'email', 'send', 'reply', 'notifications', 'notifications_active', 'volume_up', 'announcement', 'feedback', 'live_help'] },
  { key: 'time', words: 'temps date calendrier planning heure agenda', icons: ['event', 'today', 'date_range', 'event_note', 'schedule', 'access_time', 'alarm', 'timer', 'hourglass_empty', 'history', 'update', 'event_available', 'watch_later', 'perm_contact_calendar'] },
  { key: 'markers', words: 'repère statut alerte information validation favori', icons: ['flag', 'bookmark', 'bookmark_border', 'label', 'local_offer', 'info_outline', 'help_outline', 'lightbulb_outline', 'warning', 'error_outline', 'report_problem', 'check_circle', 'done_all', 'verified_user', 'new_releases', 'priority_high', 'lock', 'visibility'] },
  { key: 'actions', words: 'action bouton raccourci navigation lien outil', icons: ['touch_app', 'launch', 'open_in_new', 'add_circle_outline', 'add', 'search', 'settings', 'build', 'tune', 'filter_list', 'sort', 'refresh', 'sync', 'cloud_upload', 'cloud_download', 'get_app', 'print', 'dashboard', 'apps', 'view_module', 'home', 'explore', 'flight_takeoff'] },
  { key: 'business', words: 'business commerce vente magasin argent objectif lieu', icons: ['store', 'local_grocery_store', 'shopping_cart', 'redeem', 'local_mall', 'attach_money', 'euro_symbol', 'credit_card', 'work', 'business', 'business_center', 'domain', 'location_on', 'place', 'map', 'my_location', 'local_shipping', 'loyalty', 'card_giftcard', 'wb_incandescent'] }
]

/**
 * Choix de l'icône du titre d'un widget : celle du widget (null), aucune ('none') ou une icône de la liste
 */
export default {
  name: 'IconPicker',

  components: { Tooltip },

  directives: { ClickOutside },

  props: {
    value: { type: String, default: null },
    defaultIcon: { type: String, default: 'widgets' }
  },

  data() {
    return { open: false, query: '' }
  },

  computed: {
    filtered() {
      const q = this.query.trim().toLowerCase()
      if (!q) return CATEGORIES
      return CATEGORIES
        .map(cat => {
          const catMatch = cat.words.includes(q) || this.$t(`dash.overview.dashboards.icon_cat.${cat.key}`).toLowerCase().includes(q)
          return { ...cat, icons: catMatch ? cat.icons : cat.icons.filter(ic => ic.replace(/_/g, ' ').includes(q)) }
        })
        .filter(cat => cat.icons.length)
    }
  },

  watch: {
    open(value) {
      if (value) this.$nextTick(() => this.$refs.search && this.$refs.search.focus())
      else this.query = ''
    }
  },

  methods: {
    pick(icon) {
      this.$emit('input', icon)
      this.open = false
    },

    close() {
      this.open = false
    }
  }
}
</script>

<style scoped>
.ip {
  position: relative;
  flex: 0 0 auto;
}
.ip-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  padding: 0 2px 0 8px;
  border: 1.5px solid #ebe3f0;
  border-radius: 8px;
  background: #fff;
  color: #6b1f4d;
  cursor: pointer;
  transition: border-color 0.15s;
}
.ip-trigger:hover,
.ip-trigger.open {
  border-color: #b64985;
}
.ip-trigger .material-icons {
  font-size: 19px;
}
.ip-trigger .ip-caret {
  font-size: 18px;
  color: #9ba3b7;
}
.ip-none {
  color: #9ba3b7 !important;
}
.ip-pop {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 40;
  width: 272px;
  padding: 10px;
  border: 1px solid #f1edf5;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 10px 32px rgba(18, 24, 38, 0.16);
}
.ip-search {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 8px;
  border-radius: 8px;
  background: #f6f1fa;
}
.ip-search .material-icons {
  font-size: 17px;
  color: #9ba3b7;
}
.ip-search input {
  flex: 1;
  min-width: 0;
  height: 30px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 12.5px;
}
.ip-quick {
  display: flex;
  gap: 6px;
  margin: 8px 0 4px;
}
.ip-quick-btn {
  display: inline-flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 30px;
  border: 1.5px solid #ebe3f0;
  border-radius: 8px;
  font-size: 11.5px;
  font-weight: 600;
  color: #555e75;
  cursor: pointer;
}
.ip-quick-btn .material-icons {
  font-size: 16px;
}
.ip-quick-btn.active,
.ip-quick-btn:hover {
  border-color: #b64985;
  color: #6b1f4d;
}
.ip-list {
  max-height: 250px;
  overflow-y: auto;
  padding-right: 2px;
}
.ip-cat {
  margin: 8px 0 4px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #9ba3b7;
}
.ip-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 2px;
}
.ip-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  border-radius: 7px;
  color: #555e75;
  cursor: pointer;
}
.ip-icon:hover {
  background: #f6f1fa;
  color: #6b1f4d;
}
.ip-icon.active {
  background: #6b1f4d;
  color: #fff;
}
.ip-icon .material-icons {
  font-size: 19px;
}
.ip-empty {
  margin: 10px 0;
  font-size: 12px;
  color: #9ba3b7;
  text-align: center;
}
</style>
